import { Link } from 'react-router'
import { backState } from '#app/components/back-link.tsx'
import BlurFade from '#app/components/magicui/blur-fade.tsx'
import { LISTED_DEMOS } from '#app/features/portfolio/demos/registry.ts'
import { getAllArticles } from '#app/features/writing/articles.ts'

const BLUR_FADE_DELAY = 0.04

interface WritingCard {
	title: string
	blurb: string
	href: string
	label: string
}

// Per-slug label overrides where the first tag isn't the right badge.
const labelOverrides: Record<string, string> = {
	'declarative-accounting': 'Explainer',
}

// One flat list, one rule: anything without a buy link is a Writing — the interactive
// demos and the essays sit in the same list, distinguished only by their label. (Things
// WITH a buy link are Projects and get a card in that section instead.) A recruiter reads
// straight down; every row is a link that can be sent on its own.
//
// Demos lead (their curated order encodes the 2026-08-03 AI-Engineer pivot), then the
// articles newest-first. In dev, getAllArticles() also returns drafts so work-in-progress
// is previewable; in prod it excludes them.
function visibleWritings(): WritingCard[] {
	const demos: WritingCard[] = LISTED_DEMOS.map((d) => ({
		title: d.title,
		blurb: d.blurb,
		href: d.href,
		label: d.label,
	}))
	const articles: WritingCard[] = getAllArticles().map((a) => ({
		title: a.title,
		blurb: a.description,
		href: `/writing/${a.slug}`,
		label: a.draft
			? 'Draft'
			: (labelOverrides[a.slug] ?? a.tags?.[0] ?? 'Article'),
	}))
	return [...demos, ...articles]
}

const Writing = () => {
	const writings = visibleWritings()
	return (
		<section id="writing" className="container">
			<div className="flex min-h-0 flex-col gap-y-4 py-6">
				<BlurFade delay={BLUR_FADE_DELAY * 5}>
					<div className="flex items-baseline justify-between gap-2">
						<h2 className="text-xl font-bold">Writing</h2>
						<Link
							to="/writing"
							className="text-muted-foreground hover:text-foreground group inline-flex items-center gap-1 text-sm transition-colors"
						>
							See all
							<span
								aria-hidden
								className="transition-transform group-hover:translate-x-0.5"
							>
								→
							</span>
						</Link>
					</div>
				</BlurFade>
				<div className="flex flex-col gap-3">
					{writings.map((w, id) => (
						<BlurFade key={w.title} delay={BLUR_FADE_DELAY * 6 + id * 0.05}>
							<Link
								to={w.href}
								state={backState('/#writing')}
								className="hover:bg-muted/50 group block rounded-lg border p-4 transition-colors"
							>
								<div className="flex items-center justify-between gap-2">
									<h3 className="text-sm font-semibold">{w.title}</h3>
									<span className="text-muted-foreground text-xs">
										{w.label}
									</span>
								</div>
								<p className="text-muted-foreground mt-1 text-sm text-pretty">
									{w.blurb}
								</p>
							</Link>
						</BlurFade>
					))}
				</div>
			</div>
		</section>
	)
}

export default Writing
